import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { FixedSizeList, type ListChildComponentProps } from 'react-window'
import type { WeldTelemetrySampleRow } from '../../utils/weldTelemetryClient'
import { useUnitSetting } from '../../utils/userUnitSetting'
import {
  convertWeldDataValue,
  getParameterUnitSuffix
} from '../../utils/weldUnitUtils'
import { getPassAnalyticsColumnLabel } from '../../utils/weldParameterHeaderLabels'
import {
  cycleSort,
  INITIAL_SORT_STATE,
  sortSamples,
  type SortState
} from '../../utils/passAnalyticsSort'
import {
  NULL_STATS,
  STATS_COLUMN_KEYS,
  type ParamStats,
  type StatsColumnKey
} from '../../utils/passAnalyticsStats'
import { applyToleranceToSampleValue } from '../../utils/pqr/applyPqrTolerance'
import {
  applyColumnFilters,
  buildFilterOptions,
  EMPTY_FILTER,
  isFilterActive,
  type ColumnFiltersMap,
  type ColumnFilterState
} from '../../utils/passAnalyticsFilters'
import ColumnFilterPopover from '../ui/ColumnFilterPopover'

export interface PassAnalyticsTableProps {
  samples: WeldTelemetrySampleRow[]
  passLabel: string
  /**
   * Optional whitelist of column keys to render. Omit (or pass `null`) to render
   * the full default set. Order of the prop is ignored — the table preserves
   * its canonical column order regardless of selection order.
   */
  visibleKeys?: string[] | null
  /**
   * Map of `StatsColumnKey -> {min, avg, max}` rendered stacked under each
   * metric column header. When omitted (or every entry is `NULL_STATS`) no
   * stats line is shown.
   */
  headerStats?: Partial<Record<StatsColumnKey, ParamStats>>
  /**
   * Selection model for the leading checkbox column. When omitted, the
   * checkbox column is hidden. The page lifts state so it can drive the
   * Calculate min/max recompute.
   */
  selection?: {
    selectedSampleIds: Set<number>
    onToggle: (sampleId: number) => void
    onToggleAll: () => void
  }
  /**
   * Optional opaque key used to scope filter persistence in `sessionStorage`.
   * Pass-analytics page builds this from `(project, pipeSet, weld, pass)` so
   * each (weld, pass) keeps its own filter draft for the session. When the
   * key is empty, filters live in component state only.
   */
  filtersStorageKey?: string
  /**
   * Filenames of the TXT files that contributed to this upload, indexed by
   * `sample.sourceFileIndex`. When 2+ entries are present, the table renders
   * a thin divider above the first row of each new file and surfaces the
   * filename in a hover tooltip. Single-file uploads (length < 2) render
   * exactly as before.
   */
  sourceFilenames?: string[]
  /**
   * Saved Welding Parameters tolerance % — reduces displayed metric cell values
   * client-side only. Sort/filter operate on raw underlying values.
   */
  toleranceBoundary?: number
}

interface PassAnalyticsColumnRegistryEntry {
  key: string
  label: string
  unitKey?: string
}

/**
 * Static column registry used by the Columns selector dialog. Each entry maps
 * 1:1 to a column produced by `makeColumns(...)`, grouped so the modal can
 * render readable sections. Keep this in sync with `makeColumns` below.
 */
const PASS_ANALYTICS_COLUMN_REGISTRY: {
  group: string
  columns: PassAnalyticsColumnRegistryEntry[]
}[] = [
  {
    group: 'Pinned',
    columns: [
      { key: 'sl-no', label: 'Sl.no' },
      { key: 'event', label: 'Event' },
      { key: 'time', label: 'Time' }
    ]
  },
  {
    group: 'Position',
    columns: [
      { key: 'tilt', label: 'Tilt', unitKey: 'tilt' },
      { key: 'pass', label: 'Pass' },
      { key: 'torch', label: 'Torch' },
      { key: 'bugType', label: 'Bug Type' },
      { key: 'distance', label: 'Distance', unitKey: 'distance' }
    ]
  },
  {
    group: 'Welding parameters',
    columns: [
      { key: 'travelSpeed', label: 'Travel Speed', unitKey: 'travelSpeed' },
      { key: 'volts', label: 'Voltage', unitKey: 'volts' },
      { key: 'amps', label: 'Current', unitKey: 'amps' },
      { key: 'wireIpm', label: 'Wire Speed', unitKey: 'wireIpm' },
      { key: 'widthMil', label: 'Oscillation Width', unitKey: 'widthMil' },
      { key: 'targetVlt', label: 'Target', unitKey: 'targetVlt' },
      { key: 'csBias', label: 'Horizontal Bias', unitKey: 'csBias' },
      { key: 'bpm', label: 'Frequency', unitKey: 'bpm' }
    ]
  },
  {
    group: 'Totals',
    columns: [
      { key: 'totWireIn', label: 'Total Wire Consumed', unitKey: 'totWireIn' },
      { key: 'trueEnergy', label: 'True Energy', unitKey: 'trueEnergy' },
      { key: 'heat', label: 'Heat Input', unitKey: 'rprtHeat' },
      { key: 'duration', label: 'Duration', unitKey: 'duration' }
    ]
  }
]

/** Unit-aware labels for the Pass Analytics column selector (respects Imperial/Metric). */
export function getPassAnalyticsColumnGroups(): {
  group: string
  columns: { key: string; label: string }[]
}[] {
  return PASS_ANALYTICS_COLUMN_REGISTRY.map((group) => ({
    group: group.group,
    columns: group.columns.map((col) => ({
      key: col.key,
      label: getPassAnalyticsColumnLabel(col.label, col.unitKey)
    }))
  }))
}

/** @deprecated Use `getPassAnalyticsColumnGroups()` for unit-aware labels. */
export const PASS_ANALYTICS_COLUMN_GROUPS = PASS_ANALYTICS_COLUMN_REGISTRY.map((group) => ({
  group: group.group,
  columns: group.columns.map(({ key, label }) => ({ key, label }))
}))

export const PASS_ANALYTICS_ALL_COLUMN_KEYS: string[] = PASS_ANALYTICS_COLUMN_REGISTRY.flatMap(
  (group) => group.columns.map((c) => c.key)
)

/**
 * Default visible columns. Duration is intentionally OFF by default — users
 * can re-enable it via the Columns dialog. Order matches the column listing
 * communicated in floorsheet-pass-analytics-fixes plan.
 */
export const PASS_ANALYTICS_DEFAULT_COLUMN_KEYS: string[] = PASS_ANALYTICS_ALL_COLUMN_KEYS.filter(
  (k) => k !== 'duration'
)

const STATS_KEY_SET = new Set<string>(STATS_COLUMN_KEYS as readonly string[])

/**
 * Threshold above which we switch to a virtualised `<div>`-based body. The
 * virtualised path has to fake `position: sticky` via `transform: translateX`
 * because react-window absolute-positions every row, and that proxy was
 * causing the Sl.no / Checkbox columns to drift while scrolling horizontally.
 * Until we either drop the row count (typical TXT exports stay under 4k
 * samples per pass) or switch to a virtualiser that supports native sticky,
 * we keep the bar high so the native CSS sticky path drives every real
 * upload. The threshold itself is still consulted by a single
 * virtualisation test that exercises the fallback path.
 */
const VIRTUALISATION_THRESHOLD = 5000

/** Fixed row metrics for the virtualised body. Width is in px. */
const VIRTUAL_ROW_HEIGHT = 52
const VIRTUAL_BODY_HEIGHT = 480

const SELECT_COL_WIDTH = 44

interface ColumnDef {
  key: string
  label: string
  paramKey?: string
  sticky?: boolean
  align?: 'left' | 'right' | 'center'
  /** false for the Pass column which is a constant value per page. */
  sortable?: boolean
  /** Whether the column shows a filter funnel in its header. */
  filterable?: boolean
  /** Fixed width in px (used by the virtualised body for column alignment). */
  width: number
  /**
   * `originalIndex` is the row's 1-based position in the unsorted, unfiltered
   * source array (used to keep Sl.no static across sort/filter changes).
   */
  format?: (
    sample: WeldTelemetrySampleRow,
    originalIndex: number,
    baseTime: number
  ) => React.ReactNode
}

const EM_DASH = <span className="text-[#C9CDD4]">—</span>

function fmtNumber(value: number | null | undefined, digits = 2): React.ReactNode {
  if (value === null || value === undefined || !Number.isFinite(value)) return EM_DASH
  const rounded = Math.abs(value - Math.round(value)) < 1e-9
  return (
    <span className="tabular-nums">
      {rounded ? String(Math.round(value)) : Number(value).toFixed(digits)}
    </span>
  )
}

function fmtConverted(
  rawValue: number | null | undefined,
  paramKey: string,
  digits = 2
): React.ReactNode {
  return fmtNumber(convertWeldDataValue(rawValue ?? null, paramKey), digits)
}

function fmtStatsConverted(
  rawValue: number | null | undefined,
  paramKey: string,
  digits: number,
  toleranceBoundary: number
): React.ReactNode {
  const converted = convertWeldDataValue(rawValue ?? null, paramKey)
  if (converted === null || !Number.isFinite(converted)) return EM_DASH
  return fmtNumber(applyToleranceToSampleValue(converted, toleranceBoundary), digits)
}

/**
 * Render the sample's wall-clock UTC timestamp as `27-May-2026, 5:08:34 PM`.
 * Built from `formatToParts` so we always emit a stable 3-letter month
 * abbreviation (Node's modern Intl/CLDR returns `'Sept'` for September,
 * which would break the `DD-MMM-YYYY` shape if we relied on `format()`).
 */
const TIME_FMT_PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
  second: '2-digit',
  hour12: true
})

const SHORT_MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
] as const

function formatRecordedAt(iso: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const parts = TIME_FMT_PARTS.formatToParts(d)
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? ''
  const day = get('day')
  // Pin to a 3-letter abbreviation regardless of the runtime's CLDR (some
  // Node.js builds emit 4-letter `Sept`).
  const month = SHORT_MONTHS[d.getUTCMonth()]
  const year = get('year')
  const hour = get('hour')
  const minute = get('minute')
  const second = get('second')
  const dayPeriod = get('dayPeriod').toUpperCase()
  return `${day}-${month}-${year}, ${hour}:${minute}:${second} ${dayPeriod}`
}

function fmtTime(sample: WeldTelemetrySampleRow): React.ReactNode {
  const formatted = formatRecordedAt(sample.recordedAt)
  if (!formatted) return EM_DASH
  return <span className="tabular-nums whitespace-nowrap">{formatted}</span>
}

function fmtBugType(sample: WeldTelemetrySampleRow): React.ReactNode {
  const bugType = sample.bugType
  if (bugType !== 'CW' && bugType !== 'CCW') return EM_DASH
  const cls =
    bugType === 'CW'
      ? 'bg-accent-50 text-accent-700 ring-accent-200'
      : 'bg-primary-50 text-primary-700 ring-primary-200'
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${cls}`}
    >
      {bugType}
    </span>
  )
}

function fmtTorch(sample: WeldTelemetrySampleRow): React.ReactNode {
  const cls =
    sample.torch === 'Lead'
      ? 'bg-accent-50 text-accent-700 ring-accent-200'
      : 'bg-warning-50 text-warning-700 ring-warning-200'
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${cls}`}
    >
      {sample.torch}
    </span>
  )
}

function fmtHeat(sample: WeldTelemetrySampleRow, toleranceBoundary: number): React.ReactNode {
  const raw = sample.rprtHeat ?? sample.calcHeat
  return fmtStatsConverted(raw, 'rprtHeat', 2, toleranceBoundary)
}

/**
 * Column registry. The order here drives the rendered column order:
 *   Sl.no | Event | Time | Tilt | Pass | Torch | Distance | metrics… | Duration
 *
 * Only `sl-no` is sticky-leftmost (alongside the optional Select column).
 * `Event` and `Time` scroll horizontally with the rest of the position
 * group so wider tables still keep the row identifier pinned without
 * stealing too much horizontal real estate.
 */
function makeColumns(passLabel: string, toleranceBoundary = 0): ColumnDef[] {
  // Widths mirror WeldPassDetailTable.tsx so the Min/Avg/Max grid in metric
  // columns has enough room to render Min/Avg/Max as a horizontal triplet
  // (min-w-[300px] effective via 300px width) and the sticky leftmost columns
  // line up with the reference table. The Time column is widened to fit the
  // formatted UTC string `DD-MMM-YYYY, h:mm:ss A`.
  return [
    { key: 'sl-no', label: 'Sl.no', sticky: true, align: 'center', width: 80, format: (_s, originalIndex) => <span className="tabular-nums">{originalIndex}</span> },
    { key: 'event', label: 'Event', align: 'center', width: 140, filterable: true, format: (s) => <span className="tabular-nums">{s.evtNo}</span> },
    { key: 'time', label: 'Time', align: 'center', width: 220, filterable: true, format: fmtTime },
    { key: 'tilt', label: 'Tilt', paramKey: 'tilt', align: 'center', width: 100, filterable: true, format: (s) => fmtConverted(s.tilt, 'tilt', 2) },
    { key: 'pass', label: 'Pass', sortable: false, filterable: true, align: 'center', width: 120, format: () => <span>{passLabel}</span> },
    { key: 'torch', label: 'Torch', align: 'center', width: 120, filterable: true, format: fmtTorch },
    { key: 'bugType', label: 'Bug Type', align: 'center', width: 120, filterable: true, format: fmtBugType },
    { key: 'distance', label: 'Distance', paramKey: 'distance', align: 'center', width: 150, filterable: true, format: (s) => fmtConverted(s.distance, 'distance', 2) },
    { key: 'travelSpeed', label: 'Travel Speed', paramKey: 'travelSpeed', align: 'center', width: 300, filterable: true, format: (s) => fmtStatsConverted(s.travelSpeed, 'travelSpeed', 2, toleranceBoundary) },
    { key: 'volts', label: 'Voltage', paramKey: 'volts', align: 'center', width: 300, filterable: true, format: (s) => fmtStatsConverted(s.volts, 'volts', 2, toleranceBoundary) },
    { key: 'amps', label: 'Current', paramKey: 'amps', align: 'center', width: 300, filterable: true, format: (s) => fmtStatsConverted(s.amps, 'amps', 1, toleranceBoundary) },
    { key: 'wireIpm', label: 'Wire Speed', paramKey: 'wireIpm', align: 'center', width: 300, filterable: true, format: (s) => fmtStatsConverted(s.wireIpm, 'wireIpm', 0, toleranceBoundary) },
    { key: 'widthMil', label: 'Oscillation Width', paramKey: 'widthMil', align: 'center', width: 300, filterable: true, format: (s) => fmtStatsConverted(s.widthMil, 'widthMil', 1, toleranceBoundary) },
    { key: 'targetVlt', label: 'Target', paramKey: 'targetVlt', align: 'center', width: 300, filterable: true, format: (s) => fmtStatsConverted(s.targetVlt, 'targetVlt', 2, toleranceBoundary) },
    { key: 'csBias', label: 'Horizontal Bias', paramKey: 'csBias', align: 'center', width: 300, filterable: true, format: (s) => fmtStatsConverted(s.csBias, 'csBias', 2, toleranceBoundary) },
    { key: 'bpm', label: 'Frequency', paramKey: 'bpm', align: 'center', width: 300, filterable: true, format: (s) => fmtStatsConverted(s.bpm, 'bpm', 1, toleranceBoundary) },
    { key: 'totWireIn', label: 'Total Wire Consumed', paramKey: 'totWireIn', align: 'center', width: 300, filterable: true, format: (s) => fmtStatsConverted(s.totWireIn, 'totWireIn', 2, toleranceBoundary) },
    { key: 'trueEnergy', label: 'True Energy', paramKey: 'trueEnergy', align: 'center', width: 300, filterable: true, format: (s) => fmtStatsConverted(s.trueEnergy, 'trueEnergy', 2, toleranceBoundary) },
    { key: 'heat', label: 'Heat Input', paramKey: 'rprtHeat', align: 'center', width: 300, filterable: true, format: (s) => fmtHeat(s, toleranceBoundary) },
    { key: 'duration', label: 'Duration', paramKey: 'duration', align: 'center', width: 300, filterable: true, format: (s) => fmtNumber(s.duration, 3) }
  ]
}

function isSortable(col: ColumnDef): boolean {
  return col.sortable !== false
}

/**
 * Mirrors WeldPassDetailTable.tsx: a vertical divider sits on the LEFT edge of
 * every metric column (the ones eligible for Min/Avg/Max stats), separating
 * the Position group from the parameter group and each metric column from the
 * next.
 */
function hasLeadingDivider(col: ColumnDef): boolean {
  return STATS_KEY_SET.has(col.key)
}

function headerLabel(col: ColumnDef): string {
  if (!col.paramKey) return col.label
  return getPassAnalyticsColumnLabel(col.label, col.paramKey)
}

function alignClass(_align: ColumnDef['align']): string {
  // Mirror WeldPassDetailTable: every header and cell is center-aligned so the
  // Min/Avg/Max grid below each metric label lines up with the data column.
  return 'text-center'
}

function fmtStatValue(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '—'
  const rounded = Math.abs(value - Math.round(value)) < 1e-9
  return rounded ? String(Math.round(value)) : Number(value).toFixed(2)
}

interface SortIndicatorProps {
  active: boolean
  direction: SortState['direction']
  /**
   * `false` for the constant-value `Pass` column so it renders no chevron
   * at all (mirrors `WeldPassDetailTable.tsx`'s `renderSortIcon`). Every
   * other header gets a faded `ChevronUp` by default so the sort
   * affordance is visible before the user clicks.
   */
  sortable: boolean
}

function SortIndicator({ active, direction, sortable }: SortIndicatorProps) {
  if (!sortable) return null
  if (!active || direction === null) {
    return (
      <ChevronUp
        data-testid="pa-sort-inactive"
        className="ml-1 inline-block h-3 w-3 text-gray-400"
        aria-hidden
      />
    )
  }
  if (direction === 'asc') {
    return (
      <ChevronUp
        data-testid="pa-sort-asc"
        className="ml-1 inline-block h-3 w-3 text-blueprint-blue"
        aria-hidden
      />
    )
  }
  return (
    <ChevronDown
      data-testid="pa-sort-desc"
      className="ml-1 inline-block h-3 w-3 text-blueprint-blue"
      aria-hidden
    />
  )
}

interface HeaderCellProps {
  col: ColumnDef
  active: boolean
  direction: SortState['direction']
  stats?: ParamStats
  filterControl?: React.ReactNode
  onHeaderClick: (col: ColumnDef) => void
}

/**
 * Renders the inner contents of a header cell as two rows:
 *   1. label + chevron (inside the sort button) + filter funnel — all inline,
 *      so the funnel sits flush against the chevron with no extra column width.
 *   2. Optional Min/Avg/Max grid below.
 *
 * The filter funnel is a SIBLING of the sort button (not nested) so the markup
 * stays valid HTML and tests can still target the sort button by role / by
 * `pa-sort-trigger-${col.key}`.
 */
function HeaderCell({ col, active, direction, stats, filterControl, onHeaderClick }: HeaderCellProps) {
  const sortable = isSortable(col)
  const label = headerLabel(col)
  const hasStats =
    stats !== undefined &&
    (stats.min !== null || stats.avg !== null || stats.max !== null)
  return (
    <span className="flex w-full flex-col items-center justify-center gap-1 leading-tight">
      <span className="inline-flex items-center justify-center gap-0.5 whitespace-nowrap">
        {sortable ? (
          <button
            type="button"
            data-testid={`pa-sort-trigger-${col.key}`}
            onClick={() => onHeaderClick(col)}
            className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 transition-colors hover:text-gray-800"
          >
            {label}
            <SortIndicator active={active} direction={direction} sortable />
          </button>
        ) : (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-600">
            {label}
          </span>
        )}
        {filterControl}
      </span>
      {hasStats ? (
        <span
          data-testid={`pa-header-stats-${col.key}`}
          className="grid w-full grid-cols-3 text-center text-xs font-normal text-gray-500"
        >
          <span className="tabular-nums">Min: {fmtStatValue(stats!.min)}</span>
          <span className="tabular-nums">Avg: {fmtStatValue(stats!.avg)}</span>
          <span className="tabular-nums">Max: {fmtStatValue(stats!.max)}</span>
        </span>
      ) : null}
    </span>
  )
}

function readPersistedFilters(key: string): ColumnFiltersMap {
  if (!key || typeof window === 'undefined') return {}
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    const out: ColumnFiltersMap = {}
    for (const [k, v] of Object.entries(parsed)) {
      if (!v || typeof v !== 'object') continue
      const entry = v as Partial<ColumnFilterState>
      const values =
        entry.values === null
          ? null
          : Array.isArray(entry.values)
          ? entry.values.filter((x) => typeof x === 'string')
          : null
      const text = typeof entry.text === 'string' ? entry.text : ''
      out[k] = { values, text }
    }
    return out
  } catch {
    return {}
  }
}

function writePersistedFilters(key: string, filters: ColumnFiltersMap): void {
  if (!key || typeof window === 'undefined') return
  try {
    const compact: ColumnFiltersMap = {}
    for (const [k, v] of Object.entries(filters)) {
      if (isFilterActive(v)) compact[k] = v
    }
    if (Object.keys(compact).length === 0) {
      window.sessionStorage.removeItem(key)
    } else {
      window.sessionStorage.setItem(key, JSON.stringify(compact))
    }
  } catch {
    // sessionStorage may be unavailable; we silently no-op so the UI still
    // works against in-memory state.
  }
}

export function PassAnalyticsTable({
  samples,
  passLabel,
  visibleKeys,
  headerStats,
  selection,
  filtersStorageKey,
  sourceFilenames,
  toleranceBoundary = 0
}: PassAnalyticsTableProps) {
  useUnitSetting()
  const allColumns = useMemo(
    () => makeColumns(passLabel, toleranceBoundary),
    [passLabel, toleranceBoundary]
  )
  const columns = useMemo(() => {
    if (!visibleKeys) return allColumns
    const allowed = new Set(visibleKeys)
    return allColumns.filter((c) => allowed.has(c.key))
  }, [allColumns, visibleKeys])
  const [sortState, setSortState] = useState<SortState>(INITIAL_SORT_STATE)

  // Per-column filter state. Re-hydrated from sessionStorage when the storage
  // key changes (page mount, navigating between weld/pass combos).
  const [filters, setFilters] = useState<ColumnFiltersMap>(() =>
    readPersistedFilters(filtersStorageKey ?? '')
  )
  useEffect(() => {
    setFilters(readPersistedFilters(filtersStorageKey ?? ''))
  }, [filtersStorageKey])

  const persistFilters = useCallback(
    (next: ColumnFiltersMap) => {
      writePersistedFilters(filtersStorageKey ?? '', next)
    },
    [filtersStorageKey]
  )

  const setColumnFilter = useCallback(
    (columnKey: string, updater: (prev: ColumnFilterState) => ColumnFilterState) => {
      setFilters((prev) => {
        const current = prev[columnKey] ?? EMPTY_FILTER
        const nextEntry = updater(current)
        const next = { ...prev, [columnKey]: nextEntry }
        if (!isFilterActive(nextEntry)) delete next[columnKey]
        persistFilters(next)
        return next
      })
    },
    [persistFilters]
  )

  // Original 1-based position of every sample in the unsorted source array.
  // Sl.no reads from this map so it stays static under sort/filter changes
  // (a row that was originally #47 still shows "47" when the user sorts by
  // Voltage or filters by Pass).
  const originalIndexById = useMemo(() => {
    const map = new Map<number, number>()
    samples.forEach((s, i) => map.set(s.sampleId, i + 1))
    return map
  }, [samples])

  // Filter, then sort. Filtering first keeps the sortable subset smaller and
  // lets the sort stable-tie-break against the filtered subset's natural
  // ordering.
  const filteredSamples = useMemo(
    () => applyColumnFilters(samples, filters),
    [samples, filters]
  )

  // `sortSamples` reads `convertWeldDataValue` via `getSortValue`; include the
  // current unit suffix in the memo deps so a runtime unit flip re-sorts.
  const unitProbe = getParameterUnitSuffix('travelSpeed')
  const sortedSamples = useMemo(
    () => sortSamples(filteredSamples, sortState),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredSamples, sortState, unitProbe]
  )

  const baseTime = samples.length > 0 ? samples[0].sampleTime : 0
  const onHeaderClick = (col: ColumnDef) => {
    if (!isSortable(col)) return
    setSortState((prev) => cycleSort(prev, col.key))
  }

  const renderFilterControl = useCallback(
    (col: ColumnDef): React.ReactNode => {
      if (!col.filterable) return null
      const options = buildFilterOptions(samples, col.key, passLabel)
      if (options.length === 0) return null
      const current = filters[col.key] ?? EMPTY_FILTER
      return (
        <ColumnFilterPopover
          testId={`pa-filter-${col.key}`}
          options={options}
          value={current.values}
          textFilter={current.text}
          onChange={(values) =>
            setColumnFilter(col.key, (prev) => ({ ...prev, values }))
          }
          onTextFilterChange={(text) =>
            setColumnFilter(col.key, (prev) => ({ ...prev, text }))
          }
        />
      )
    },
    [samples, passLabel, filters, setColumnFilter]
  )

  const isVirtualised = sortedSamples.length > VIRTUALISATION_THRESHOLD

  const totalSampleCount = samples.length
  const selectedCount = selection ? selection.selectedSampleIds.size : 0
  const allSelected = totalSampleCount > 0 && selectedCount === totalSampleCount
  const someSelected = selectedCount > 0 && selectedCount < totalSampleCount

  return (
    <div className="flex w-full min-w-0 max-w-full flex-col gap-2">
      <div
        className="w-full min-w-0 max-w-full overflow-hidden rounded-lg border border-[#E2E5EA] bg-white"
        data-testid="pa-table"
      >
        {isVirtualised ? (
          <VirtualisedBody
            columns={columns}
            samples={sortedSamples}
            originalIndexById={originalIndexById}
            baseTime={baseTime}
            sortState={sortState}
            onHeaderClick={onHeaderClick}
            headerStats={headerStats}
            selection={selection}
            allSelected={allSelected}
            someSelected={someSelected}
            renderFilterControl={renderFilterControl}
            sourceFilenames={sourceFilenames}
          />
        ) : (
          <TableBody
            columns={columns}
            samples={sortedSamples}
            originalIndexById={originalIndexById}
            baseTime={baseTime}
            sortState={sortState}
            onHeaderClick={onHeaderClick}
            headerStats={headerStats}
            selection={selection}
            allSelected={allSelected}
            someSelected={someSelected}
            renderFilterControl={renderFilterControl}
            sourceFilenames={sourceFilenames}
          />
        )}
      </div>
    </div>
  )
}

interface BodyProps {
  columns: ColumnDef[]
  samples: WeldTelemetrySampleRow[]
  originalIndexById: Map<number, number>
  baseTime: number
  sortState: SortState
  onHeaderClick: (col: ColumnDef) => void
  headerStats?: Partial<Record<StatsColumnKey, ParamStats>>
  selection?: PassAnalyticsTableProps['selection']
  allSelected: boolean
  someSelected: boolean
  renderFilterControl: (col: ColumnDef) => React.ReactNode
  /**
   * Multi-file upload manifest. When `length >= 2`, body components render a
   * thin divider above each row whose `sourceFileIndex` differs from the
   * previous row, with the source filename as a hover tooltip.
   */
  sourceFilenames?: string[]
}

/**
 * Small helper that decides whether a row sits at the boundary between two
 * files in the multi-file upload manifest. Returns the source filename for
 * the row when a boundary is present (used as the hover tooltip). The first
 * row never carries a boundary — the divider only marks transitions.
 */
function fileBoundaryFilename(
  samples: WeldTelemetrySampleRow[],
  index: number,
  sourceFilenames: string[] | undefined
): string | null {
  if (!sourceFilenames || sourceFilenames.length < 2) return null
  if (index <= 0) return null
  const current = samples[index].sourceFileIndex
  const previous = samples[index - 1].sourceFileIndex
  // Treat both `null` (legacy uploads) and `undefined` (test fixtures or
  // any caller that omits the optional field) as "no provenance" — the
  // divider only renders when BOTH neighbours have a known file index.
  if (current === null || current === undefined) return null
  if (previous === null || previous === undefined) return null
  if (current === previous) return null
  return sourceFilenames[current] ?? null
}

function statsForColumn(
  col: ColumnDef,
  headerStats?: Partial<Record<StatsColumnKey, ParamStats>>
): ParamStats | undefined {
  if (!headerStats) return undefined
  if (!STATS_KEY_SET.has(col.key)) return undefined
  return headerStats[col.key as StatsColumnKey] ?? NULL_STATS
}

function SelectAllCheckbox({
  allSelected,
  someSelected,
  onToggleAll
}: {
  allSelected: boolean
  someSelected: boolean
  onToggleAll: () => void
}) {
  const ref = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = someSelected
  }, [someSelected])
  return (
    <input
      ref={ref}
      type="checkbox"
      data-testid="pa-select-all"
      aria-label={allSelected ? 'Deselect all rows' : 'Select all rows'}
      checked={allSelected}
      onChange={onToggleAll}
      className="h-3.5 w-3.5 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
    />
  )
}

/**
 * Compute the cumulative `left` offset (in px) for a sticky column. Mirrors
 * `getStickyLeft` in WeldPassDetailTable.tsx so leading sticky columns chain
 * cleanly when the table is scrolled horizontally. Selection column (when
 * present) is always pinned at left:0 and the first sticky data column starts
 * after it.
 */
function buildStickyLeftMap(columns: ColumnDef[], hasSelection: boolean): Map<string, number> {
  const map = new Map<string, number>()
  let offset = hasSelection ? SELECT_COL_WIDTH : 0
  for (const col of columns) {
    if (!col.sticky) break
    map.set(col.key, offset)
    offset += col.width
  }
  return map
}

function TableBody({
  columns,
  samples,
  originalIndexById,
  baseTime,
  sortState,
  onHeaderClick,
  headerStats,
  selection,
  allSelected,
  someSelected,
  renderFilterControl,
  sourceFilenames
}: BodyProps) {
  const stickyLeft = useMemo(
    () => buildStickyLeftMap(columns, !!selection),
    [columns, selection]
  )
  return (
    <div
      className="w-full max-w-full overflow-x-auto overflow-y-auto"
      style={{
        maxHeight: 'calc(100vh - 280px)',
        WebkitOverflowScrolling: 'touch',
        overscrollBehaviorX: 'contain'
      }}
    >
      <table
        className="text-sm"
        style={{ borderCollapse: 'separate', borderSpacing: 0, minWidth: 'max-content' }}
      >
        <thead className="sticky top-0 z-20 bg-gray-50">
          <tr className="h-[72px] bg-gray-50">
            {selection ? (
              <th
                scope="col"
                data-testid="pa-select-header"
                className="sticky left-0 z-30 border-b border-[#E2E5EA] bg-gray-50 px-2 text-center align-middle shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
                style={{
                  position: 'sticky',
                  left: 0,
                  width: SELECT_COL_WIDTH,
                  minWidth: SELECT_COL_WIDTH,
                  zIndex: 30
                }}
              >
                <SelectAllCheckbox
                  allSelected={allSelected}
                  someSelected={someSelected}
                  onToggleAll={selection.onToggleAll}
                />
              </th>
            ) : null}
            {columns.map((col) => {
              const active = sortState.columnKey === col.key
              const direction = active ? sortState.direction : undefined
              const stats = statsForColumn(col, headerStats)
              const leftPx = stickyLeft.get(col.key)
              const stickyClasses = col.sticky
                ? 'sticky z-30 bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]'
                : ''
              const stickyStyle: React.CSSProperties = col.sticky
                ? { position: 'sticky', left: leftPx ?? 0, zIndex: 30 }
                : {}
              const filterControl = renderFilterControl(col)
              return (
                <th
                  key={col.key}
                  scope="col"
                  data-column-key={col.key}
                  data-sticky={col.sticky ? 'true' : undefined}
                  data-sort-direction={direction ?? undefined}
                  className={`border-b border-[#E2E5EA] px-4 align-middle text-xs font-medium text-gray-600 ${alignClass(col.align)} ${hasLeadingDivider(col) ? 'border-l border-[#E2E5EA]' : ''} ${stickyClasses}`}
                  style={{
                    width: col.width,
                    minWidth: col.width,
                    ...stickyStyle
                  }}
                >
                  <HeaderCell
                    col={col}
                    active={active}
                    direction={sortState.direction}
                    stats={stats}
                    filterControl={filterControl}
                    onHeaderClick={onHeaderClick}
                  />
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {samples.map((sample, rowIndex) => {
            const isSelected = selection ? selection.selectedSampleIds.has(sample.sampleId) : true
            const originalIndex = originalIndexById.get(sample.sampleId) ?? 0
            // When the previous row came from a different source TXT,
            // tag this row with a top-border divider and surface the
            // filename via the native `title` attribute. The check
            // bails out cleanly for single-file uploads (the helper
            // returns `null` when `sourceFilenames` is shorter than 2).
            //
            // NOTE: The outer `<table>` uses `border-collapse: separate`,
            // which means a `border-t-*` class on the `<tr>` renders as
            // nothing — browsers only paint borders on cells in this
            // mode. We therefore stamp the divider class onto each `<td>`
            // below (`boundaryBorderClass`) so the line is actually
            // visible. The data-attribute / title stay on the `<tr>` so
            // the boundary tests + native hover tooltip keep working.
            const boundaryFilename = fileBoundaryFilename(samples, rowIndex, sourceFilenames)
            const boundaryBorderClass = boundaryFilename ? 'border-t-2 border-blueprint-blue' : ''
            return (
              <tr
                key={sample.sampleId}
                data-testid="pa-row"
                data-selected={selection ? (isSelected ? 'true' : 'false') : undefined}
                data-file-boundary={boundaryFilename ? 'true' : undefined}
                title={
                  boundaryFilename ? `Continues from ${boundaryFilename}` : undefined
                }
                className={`h-[52px] bg-white transition-colors hover:bg-gray-50 ${
                  selection && !isSelected ? 'opacity-60' : ''
                }`}
              >
                {selection ? (
                  <td
                    data-testid={`pa-select-${sample.sampleId}`}
                    className={`sticky left-0 z-10 border-b border-gray-200 bg-white px-2 text-center align-middle shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)] ${boundaryBorderClass}`}
                    style={{
                      position: 'sticky',
                      left: 0,
                      width: SELECT_COL_WIDTH,
                      minWidth: SELECT_COL_WIDTH,
                      zIndex: 10,
                      backgroundColor: '#ffffff'
                    }}
                  >
                    <input
                      type="checkbox"
                      data-testid={`pa-select-checkbox-${sample.sampleId}`}
                      aria-label={`Select sample ${sample.sampleId}`}
                      checked={isSelected}
                      onChange={() => selection.onToggle(sample.sampleId)}
                      className="h-3.5 w-3.5 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
                    />
                  </td>
                ) : null}
                {columns.map((col) => {
                  const leftPx = stickyLeft.get(col.key)
                  const stickyClasses = col.sticky
                    ? 'sticky z-10 bg-white shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]'
                    : ''
                  const stickyStyle: React.CSSProperties = col.sticky
                    ? {
                        position: 'sticky',
                        left: leftPx ?? 0,
                        zIndex: 10,
                        backgroundColor: '#ffffff'
                      }
                    : {}
                  return (
                    <td
                      key={col.key}
                      data-testid={`pa-cell-${col.key}`}
                      data-sticky={col.sticky ? 'true' : undefined}
                      className={`border-b border-gray-200 px-4 align-middle text-xs text-gray-600 ${alignClass(col.align)} ${hasLeadingDivider(col) ? 'border-l border-[#EEF0F3]' : ''} ${stickyClasses} ${boundaryBorderClass}`}
                      style={{
                        width: col.width,
                        minWidth: col.width,
                        ...stickyStyle
                      }}
                    >
                      {col.format ? col.format(sample, originalIndex, baseTime) : null}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

interface VirtualRowData {
  columns: ColumnDef[]
  samples: WeldTelemetrySampleRow[]
  originalIndexById: Map<number, number>
  baseTime: number
  selection?: PassAnalyticsTableProps['selection']
  /**
   * Live horizontal scroll offset of the outer overflow container. Used to
   * translate the sticky cells back into view because react-window wraps
   * every row in `position: absolute` which traps native `position: sticky`.
   */
  scrollLeft: number
  /** See `BodyProps.sourceFilenames`. Threaded through for virtual rows. */
  sourceFilenames?: string[]
}

function VirtualRow({ index, style, data }: ListChildComponentProps<VirtualRowData>) {
  const { columns, samples, originalIndexById, baseTime, selection, scrollLeft, sourceFilenames } =
    data
  const sample = samples[index]
  const isSelected = selection ? selection.selectedSampleIds.has(sample.sampleId) : true
  const originalIndex = originalIndexById.get(sample.sampleId) ?? 0
  const boundaryFilename = fileBoundaryFilename(samples, index, sourceFilenames)
  return (
    <div
      role="row"
      data-testid="pa-row"
      data-selected={selection ? (isSelected ? 'true' : 'false') : undefined}
      data-file-boundary={boundaryFilename ? 'true' : undefined}
      title={boundaryFilename ? `Continues from ${boundaryFilename}` : undefined}
      style={style}
      className={`flex h-[52px] bg-white hover:bg-gray-50 ${
        selection && !isSelected ? 'opacity-60' : ''
      } ${boundaryFilename ? 'border-t-2 border-blueprint-blue' : ''}`}
    >
      {selection ? (
        <div
          role="cell"
          data-testid={`pa-select-${sample.sampleId}`}
          className="z-10 flex items-center justify-center border-b border-gray-200 bg-white px-2 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
          style={{
            width: SELECT_COL_WIDTH,
            flex: `0 0 ${SELECT_COL_WIDTH}px`,
            transform: `translateX(${scrollLeft}px)`,
            zIndex: 10,
            position: 'relative',
            backgroundColor: '#ffffff'
          }}
        >
          <input
            type="checkbox"
            data-testid={`pa-select-checkbox-${sample.sampleId}`}
            aria-label={`Select sample ${sample.sampleId}`}
            checked={isSelected}
            onChange={() => selection.onToggle(sample.sampleId)}
            className="h-3.5 w-3.5 cursor-pointer rounded border-heritage-grey text-hero-red focus:outline-none focus:ring-2 focus:ring-hero-red/40"
          />
        </div>
      ) : null}
      {columns.map((col) => {
        const stickyClasses = col.sticky
          ? 'z-10 bg-white shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]'
          : ''
        const stickyStyle: React.CSSProperties = col.sticky
          ? {
              position: 'relative',
              transform: `translateX(${scrollLeft}px)`,
              zIndex: 10,
              backgroundColor: '#ffffff'
            }
          : {}
        return (
          <div
            key={col.key}
            role="cell"
            data-testid={`pa-cell-${col.key}`}
            data-sticky={col.sticky ? 'true' : undefined}
            className={`flex items-center justify-center border-b border-gray-200 px-4 text-xs text-gray-600 ${alignClass(col.align)} ${hasLeadingDivider(col) ? 'border-l border-[#EEF0F3]' : ''} ${stickyClasses}`}
            style={{
              width: col.width,
              flex: `0 0 ${col.width}px`,
              ...stickyStyle
            }}
          >
            {col.format ? col.format(sample, originalIndex, baseTime) : null}
          </div>
        )
      })}
    </div>
  )
}

function VirtualisedBody({
  columns,
  samples,
  originalIndexById,
  baseTime,
  sortState,
  onHeaderClick,
  headerStats,
  selection,
  allSelected,
  someSelected,
  renderFilterControl,
  sourceFilenames
}: BodyProps) {
  const totalWidth =
    columns.reduce((sum, col) => sum + col.width, 0) + (selection ? SELECT_COL_WIDTH : 0)
  const stickyLeft = useMemo(
    () => buildStickyLeftMap(columns, !!selection),
    [columns, selection]
  )
  // Track horizontal scroll on the outer overflow container so we can offset
  // the sticky body cells (react-window's absolute row wrappers break native
  // `position: sticky`).
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const [scrollLeft, setScrollLeft] = useState(0)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const onScroll = () => setScrollLeft(el.scrollLeft)
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])

  const itemData = useMemo<VirtualRowData>(
    () => ({
      columns,
      samples,
      originalIndexById,
      baseTime,
      selection,
      scrollLeft,
      sourceFilenames
    }),
    [columns, samples, originalIndexById, baseTime, selection, scrollLeft, sourceFilenames]
  )

  return (
    <div
      ref={scrollRef}
      className="w-full max-w-full overflow-x-auto"
      style={{ overscrollBehaviorX: 'contain' }}
    >
      <div style={{ minWidth: totalWidth }}>
        <div role="row" className="sticky top-0 z-20 flex h-[72px] bg-gray-50">
          {selection ? (
            <div
              role="columnheader"
              data-testid="pa-select-header"
              className="sticky left-0 z-30 flex items-center justify-center border-b border-[#E2E5EA] bg-gray-50 px-2 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]"
              style={{
                position: 'sticky',
                left: 0,
                width: SELECT_COL_WIDTH,
                flex: `0 0 ${SELECT_COL_WIDTH}px`,
                zIndex: 30
              }}
            >
              <SelectAllCheckbox
                allSelected={allSelected}
                someSelected={someSelected}
                onToggleAll={selection.onToggleAll}
              />
            </div>
          ) : null}
          {columns.map((col) => {
            const active = sortState.columnKey === col.key
            const direction = active ? sortState.direction : undefined
            const stats = statsForColumn(col, headerStats)
            const leftPx = stickyLeft.get(col.key)
            const stickyClasses = col.sticky
              ? 'sticky z-30 bg-gray-50 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]'
              : ''
            const stickyStyle: React.CSSProperties = col.sticky
              ? { position: 'sticky', left: leftPx ?? 0, zIndex: 30 }
              : {}
            const filterControl = renderFilterControl(col)
            return (
              <div
                key={col.key}
                role="columnheader"
                data-column-key={col.key}
                data-sticky={col.sticky ? 'true' : undefined}
                data-sort-direction={direction ?? undefined}
                className={`flex items-center justify-center border-b border-[#E2E5EA] px-4 text-xs font-medium text-gray-600 ${alignClass(col.align)} ${hasLeadingDivider(col) ? 'border-l border-[#E2E5EA]' : ''} ${stickyClasses}`}
                style={{
                  width: col.width,
                  flex: `0 0 ${col.width}px`,
                  ...stickyStyle
                }}
              >
                <HeaderCell
                  col={col}
                  active={active}
                  direction={sortState.direction}
                  stats={stats}
                  filterControl={filterControl}
                  onHeaderClick={onHeaderClick}
                />
              </div>
            )
          })}
        </div>
        <FixedSizeList<VirtualRowData>
          height={VIRTUAL_BODY_HEIGHT}
          width={totalWidth}
          itemSize={VIRTUAL_ROW_HEIGHT}
          itemCount={samples.length}
          itemData={itemData}
          overscanCount={4}
        >
          {VirtualRow}
        </FixedSizeList>
      </div>
    </div>
  )
}

export default PassAnalyticsTable
